<template>
  <!-- 予定表の発行ボタン -->
  <div v-if="customerScheduleData">
    <div class="button-area">
      <table class="table table-bordered">
        <thead>
          <tr class="table-info">
            <th class="text-center">作成日時</th>
            <th class="text-center">更新日時</th>
            <th class="text-center">PDF出力日時</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="text-center">{{ formatFullDateTime(customerScheduleData.created_at) }}</td>
            <td class="text-center">{{ formatFullDateTime(customerScheduleData.updated_at) }}</td>
            <td class="text-center">{{ formatFullDateTime(customerScheduleData.pdf_latest_output_datetime) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="button-area">
      <!-- 顧客予定表一覧ボタン -->
      <router-link :to="{ name: 'CustomerCalendarList', query: { ...route.query, year, month } }">
        <button type="button" class="btn btn-primary">顧客予定表一覧</button>
      </router-link>
      <!-- 編集ボタン -->
      <router-link
        :to="{
          name: 'CustomerCalendarEdit',
          params: { id: CustomerCalendarId, year: customerScheduleData.work_year, month: customerScheduleData.work_month },
          query: { year: customerScheduleData.work_year, month: customerScheduleData.work_month, from: 'detail' }
        }"
      >
        <button type="button" class="btn btn-warning ms-2">編集</button>
      </router-link>

      <button class="btn btn-info" @click="generatePDF">PDFを出力</button>
      
      <!-- メールで送信ボタン -->
      <button type="button" class="btn btn-success" @click="navigateToEmailScreen" disabled="disabled">
        メールで送信
      </button>
    </div>

    <CalendarDetailJa v-if="lang === 'ja'" :customer-schedule-data="customerScheduleData" />
    <!-- <CalendarDetailEn v-if="lang === 'en'" :customer-schedule-data="customerScheduleData" /> -->
  </div>

</template>

<script setup>
import { createApp, ref, nextTick, onMounted, watchEffect } from 'vue';
import html2pdf from 'html2pdf.js'
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import CalendarDetailJaPDF from './CalendarDetailJaPDF.vue';
import {
  getCustomerCalendar,
  updateCustomerCalendarPdfStatus

} from '@/api/axios';
import CalendarDetailJa from './CalendarDetailJa.vue';

const lang = ref('ja');
const customerScheduleData = ref({});
const generatedPdfBase64 = ref('');
const router = useRouter();
const route = useRoute();
const store = useStore();
const CustomerCalendarId = route.params.id;
const year = route.query.year || route.params.year;
const month = route.query.month || route.params.month;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const readBlobAsBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.split(',')[1] || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

watchEffect(() => {
  document.title = '顧客予定表詳細';
});

const fetchCalendarDetail = async () => {
  try {
    const response = await getCustomerCalendar(CustomerCalendarId);
    customerScheduleData.value = response.data.customer_schedule;
    // lang.value = customerScheduleData.value.billing_lang_type === 1 ? 'en' : 'ja';
  } catch (error) {
    console.error('顧客予定表情報の取得に失敗しました:', error);
  }
};

// 年月日時分秒
const formatFullDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);

  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 月は0始まり
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  return `${year}年${month}月${day}日 ${hours}時${minutes}分${seconds}秒`;
};

const generatePDF = async () => {
  store.dispatch('startGlobalLoading');
  let el = null;
  let app = null;

  try {
    // コンポーネントの内容を含むdivを作成
    el = document.createElement('div');
    document.body.appendChild(el);

    // if (lang.value === 'ja') {
      app = createApp(CalendarDetailJaPDF, { CustomerCalendarId });
    // } else {
    //   app = createApp(CalendarDetailEnPDF, { CustomerCalendarId });
    // }
    // コンポーネントをマウント
    app.mount(el);

    // コンポーネントがマウントされてDOMが更新された後に次のフレームで実行
    await nextTick();

    // PDF生成オプション
    const options = {
      margin: [0, 10], // 適度な余白
      filename: `${customerScheduleData.value.m_customer.customer_nm}_${month}月度_顧客予定表`,
      image: { type: 'jpeg', quality: 0.7 }, // 圧縮率を調整
      html2canvas: { scale: 1.5 }, // 解像度を低めに設定
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    await sleep(700);

    // html2pdfを使ってPDFを生成
    await html2pdf().set(options).from(el).save();

    // PDF出力完了後にRails APIへ通知
    await updateCustomerCalendarPdfStatus(CustomerCalendarId);

    // 📌 ここでスケジュールの再取得を実行して画面を更新
    const response = await getCustomerCalendar(CustomerCalendarId);
    customerScheduleData.value = response.data.customer_schedule;
    // lang.value = customerScheduleData.value.billing_lang_type === 1 ? 'en' : 'ja';
  } catch (error) {
    console.error('PDF生成エラー:', error);
  } finally {
    // マウントしたアプリケーションをクリーンアップ
    if (app) app.unmount();
    if (el?.parentNode) el.parentNode.removeChild(el);
    store.dispatch('stopGlobalLoading');
  }
};

// メール送信時のPDF作成
const generateAndAttachPDF = async () => {
  store.dispatch('startGlobalLoading');
  let el = null;
  let app = null;

  try {
    el = document.createElement('div');
    document.body.appendChild(el);

    app = createApp(CalendarDetailJaPDF, { CustomerCalendarId });
    app.mount(el);

    await nextTick();
    await sleep(500);

    const options = {
      margin: [0, 10], // 適度な余白
      filename: `${customerScheduleData.value.m_customer.customer_nm}_${month}月度_請求書`,
      image: { type: 'jpeg', quality: 0.7 }, // 圧縮率を調整
      html2canvas: { scale: 1.5 }, // 解像度を低めに設定
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    const pdfBlob = await html2pdf().set(options).from(el).outputPdf('blob');
    generatedPdfBase64.value = await readBlobAsBase64(pdfBlob);
  } catch (error) {
    console.error('❌ PDF生成エラー:', error);
    throw error;
  } finally {
    if (app) app.unmount();
    if (el?.parentNode) el.parentNode.removeChild(el);
    store.dispatch('stopGlobalLoading');
  }
};

// PDFが作成されるのを待ってからメール送信画面へ遷移する
const navigateToEmailScreen = async () => {
  try {
    await generateAndAttachPDF();  // ✅ PDF生成完了まで待つ

    sessionStorage.setItem("pdfData", generatedPdfBase64.value);  // ✅ sessionStorage に保存

    router.push({ 
      name: 'BuildingWorkMonthlyBillingEmailCreate',
      params: { id: parseInt(customerScheduleData.value.id), year: year, month: month },
      query: { ...route.query, year, month }  // ❌ `pdfData` を query に入れない
    });
  } catch (error) {
    console.error("❌ PDF 生成中にエラーが発生:", error);
  }
};

onMounted(fetchCalendarDetail);
</script>

<style scoped>
.button-area{
  width: 70%;
  margin: 20px auto;
}

.button-area {
  text-align: right;
  margin-bottom: 20px;
}

.button-area .btn {
  margin-left: 10px;
}
</style>
